import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, LogOut, Calendar, LayoutDashboard, Building2, ShieldCheck } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#EBEBEB] h-20 flex items-center">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl w-full mx-auto flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center">
          <img src="/ixitrip-wordmark.png" alt="ixitrip — travel simplified" className="h-12 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm text-[#222222]">
          <Link to="/search" data-testid="nav-search" className="hover:text-[#FF385C] transition-colors">Stays</Link>
          <Link to="/services" data-testid="nav-services" className="hover:text-[#FF385C] transition-colors">Travel add-ons</Link>
          <Link to="/host/properties" data-testid="nav-list-property" className="hover:text-[#FF385C] transition-colors">List your property</Link>
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Button
                data-testid="header-login-btn"
                variant="ghost"
                className="rounded-full hidden sm:inline-flex"
                onClick={() => navigate("/login")}
              >
                Log in
              </Button>
              <Button
                data-testid="header-signup-btn"
                className="rounded-full bg-[#FF385C] hover:bg-[#D90B38] text-white"
                onClick={() => navigate("/register")}
              >
                Sign up
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-user-menu"
                  className="flex items-center gap-2 border border-[#DDDDDD] rounded-full pl-3 pr-1 py-1 hover:shadow-md transition-shadow"
                >
                  <span className="text-sm font-medium hidden sm:inline">{user.name}</span>
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-[#222222] text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[#717171] text-xs">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.is_admin && (
                  <>
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="text-[#FF385C] font-semibold">
                      <ShieldCheck className="w-4 h-4 mr-2" />Admin panel
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem data-testid="menu-my-bookings" onClick={() => navigate("/bookings")}>
                  <Calendar className="w-4 h-4 mr-2" />My bookings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-my-inquiries" onClick={() => navigate("/inquiries")}>
                  <Calendar className="w-4 h-4 mr-2" />My inquiries
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[#717171] text-xs">Hosting</DropdownMenuLabel>
                <DropdownMenuItem data-testid="menu-host-dashboard" onClick={() => navigate("/host")}>
                  <LayoutDashboard className="w-4 h-4 mr-2" />Host dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-host-properties" onClick={() => navigate("/host/properties")}>
                  <Building2 className="w-4 h-4 mr-2" />My properties
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-host-inquiries" onClick={() => navigate("/host/inquiries")}>
                  <Calendar className="w-4 h-4 mr-2" />Host inquiries
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-host-bookings" onClick={() => navigate("/host/bookings")}>
                  <Calendar className="w-4 h-4 mr-2" />Host bookings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-host-subscription" onClick={() => navigate("/host/subscription")}>
                  <Building2 className="w-4 h-4 mr-2" />Subscription
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
